import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { useSeo } from "../lib/ui";

const input =
  "w-full border border-white/12 bg-[#0b0b0b] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-ochre";
const label = "mono block text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400";

const EMPTY = {
  legal_name: "", category: "", registration_number: "", practising_certificate: "", counties: "",
  indemnity: "", contact_email: "", contact_phone: "", website: "", note: "",
  licence_is_current: false, consent_to_verify: false,
};

export default function ProfessionalsJoin() {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useSeo({
    title: "Ask to be listed — The Verified Professional Network",
    description:
      "Hold a current Kenyan licence, registration or practising certificate? Put yourself forward for the register. We check your number against your own regulator before anything is published.",
  });

  useEffect(() => {
    api.get("/professionals/join").then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const ready =
    form.legal_name.trim() &&
    form.category &&
    form.registration_number.trim() &&
    form.contact_email.trim() &&
    form.licence_is_current &&
    form.consent_to_verify;

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSending(true);
      const { data } = await api.post("/professionals/join", {
        ...form,
        counties: form.counties.split(",").map((c) => c.trim()).filter(Boolean),
      });
      setDone(data);
      toast.success("Received. Nothing is published until we have checked your register.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSending(false);
    }
  };

  if (!meta) return <div className="min-h-[60vh] bg-black" data-testid="join-loading" />;

  if (done) {
    return (
      <div className="bg-black pb-24 pt-16" data-testid="join-done">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <p className="mono inline-flex items-center gap-2 text-[0.58rem] uppercase tracking-[0.16em] text-ochre">
            <Check size={12} /> Received, not published
          </p>
          <h1 className="display-2 mt-5">We Will Check It, Then We Will List You</h1>
          <p className="body-copy mt-7">{done.message}</p>
          <a href={done.register_url} target="_blank" rel="noreferrer"
            className="mono mt-7 inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.16em] text-ochre hover:text-white"
            data-testid="join-done-register">
            The {done.regulator} register <ExternalLink size={12} />
          </a>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/professionals" className="btn-primary" data-testid="join-done-back">
              Back to the register <ArrowRight size={14} />
            </Link>
            <Link to="/" className="btn-ghost">Home</Link>
          </div>
        </div>
      </div>
    );
  }

  const chosen = meta.categories.find((c) => c.id === form.category);

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="professionals-join-page">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <p className="overline text-ochre">The verified professional network</p>
        <h1 className="display-1 mt-5 max-w-3xl">{meta.heading}</h1>
        <p className="body-copy mt-8 max-w-3xl">{meta.intro}</p>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.3fr_1fr]">
          <form onSubmit={submit} className="space-y-7" data-testid="join-form">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className={label} htmlFor="legal_name">Full legal or practice name</label>
                <input id="legal_name" className={`${input} mt-3`} value={form.legal_name}
                  onChange={set("legal_name")} data-testid="join-legal-name" required />
              </div>
              <div>
                <label className={label} htmlFor="category">What you are licensed as</label>
                <select id="category" className={`${input} mt-3`} value={form.category}
                  onChange={set("category")} data-testid="join-category" required>
                  <option value="">Choose your profession</option>
                  {meta.categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {chosen ? (
              <div className="border-l-2 border-ochre bg-[#0b0b0b] p-5" data-testid="join-category-note">
                <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-ochre">{chosen.regulator}</p>
                <p className="mt-3 text-xs leading-relaxed text-zinc-400">
                  <span className="text-white">What you need to hold:</span> {chosen.needs}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                  <span className="text-white">What we would list you for:</span> {chosen.for}
                </p>
                <a href={chosen.register_url} target="_blank" rel="noreferrer"
                  className="mono mt-4 inline-flex items-center gap-2 text-[0.55rem] uppercase tracking-[0.14em] text-ochre hover:text-white"
                  data-testid="join-category-register">
                  The register we would check <ExternalLink size={11} />
                </a>
              </div>
            ) : null}

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className={label} htmlFor="registration_number">Registration or licence number</label>
                <input id="registration_number" className={`${input} mt-3`} value={form.registration_number}
                  onChange={set("registration_number")} data-testid="join-registration-number" required />
              </div>
              <div>
                <label className={label} htmlFor="practising_certificate">
                  Practising certificate and year <span className="text-zinc-600">(if you hold one)</span>
                </label>
                <input id="practising_certificate" className={`${input} mt-3`} value={form.practising_certificate}
                  onChange={set("practising_certificate")} data-testid="join-practising-certificate" />
              </div>
              <div>
                <label className={label} htmlFor="counties">Counties you cover</label>
                <input id="counties" className={`${input} mt-3`} value={form.counties} onChange={set("counties")}
                  placeholder="Nairobi, Kiambu, Machakos" data-testid="join-counties" />
              </div>
              <div>
                <label className={label} htmlFor="indemnity">
                  Professional indemnity <span className="text-zinc-600">(insurer and cover)</span>
                </label>
                <input id="indemnity" className={`${input} mt-3`} value={form.indemnity}
                  onChange={set("indemnity")} data-testid="join-indemnity" />
              </div>
              <div>
                <label className={label} htmlFor="contact_email">Email</label>
                <input id="contact_email" type="email" className={`${input} mt-3`} value={form.contact_email}
                  onChange={set("contact_email")} data-testid="join-email" required />
              </div>
              <div>
                <label className={label} htmlFor="contact_phone">Phone</label>
                <input id="contact_phone" className={`${input} mt-3`} value={form.contact_phone}
                  onChange={set("contact_phone")} data-testid="join-phone" />
              </div>
            </div>

            <div>
              <label className={label} htmlFor="website">Website or practice page</label>
              <input id="website" className={`${input} mt-3`} value={form.website} onChange={set("website")}
                data-testid="join-website" />
            </div>

            <div>
              <label className={label} htmlFor="note">Anything else worth knowing</label>
              <textarea id="note" rows={4} className={`${input} mt-3 resize-none`} value={form.note}
                onChange={set("note")} data-testid="join-note"
                placeholder="The work you actually do, and where football or county projects fit into it." />
            </div>

            <div className="border border-white/10 bg-[#0b0b0b] p-6" data-testid="join-declarations">
              <label className="flex cursor-pointer gap-3 text-xs leading-relaxed text-zinc-300">
                <input type="checkbox" checked={form.licence_is_current} onChange={set("licence_is_current")}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#E5A91E]" data-testid="join-licence-current" />
                <span>
                  My licence, registration or practising certificate is current today, and the number above is
                  mine.
                </span>
              </label>
              <label className="mt-5 flex cursor-pointer gap-3 text-xs leading-relaxed text-zinc-300">
                <input type="checkbox" checked={form.consent_to_verify} onChange={set("consent_to_verify")}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#E5A91E]" data-testid="join-consent-verify" />
                <span>
                  You may check that number against my regulator's public register, and publish the regulator,
                  the number and the date you checked it beside my name.
                </span>
              </label>
            </div>

            <button type="submit" disabled={!ready || sending}
              className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="join-submit">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              Put me forward for the register
            </button>
            {!ready ? (
              <p className="mono text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500" data-testid="join-hint">
                Name, profession, number, email and both declarations are needed
              </p>
            ) : null}
          </form>

          <aside className="space-y-8">
            <div className="border-l-2 border-maasai bg-[#0b0b0b] p-6" data-testid="join-no-documents">
              <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-maasai">Do not send documents</p>
              <p className="mt-3 text-xs leading-relaxed text-zinc-400">{meta.no_documents}</p>
            </div>

            <div data-testid="join-next">
              <p className={label}>What happens next</p>
              <ol className="mt-4 space-y-4">
                {meta.what_happens_next.map((t, i) => (
                  <li key={t} className="flex gap-4 text-xs leading-relaxed text-zinc-400">
                    <span className="mono shrink-0 text-ochre">{String(i + 1).padStart(2, "0")}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div data-testid="join-terms">
              <p className={label}>What being listed means</p>
              <ul className="mt-4 space-y-3">
                {meta.terms.map((t) => (
                  <li key={t} className="flex gap-3 text-xs leading-relaxed text-zinc-400">
                    <span className="mt-[6px] h-1.5 w-1.5 shrink-0 bg-ochre" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs leading-relaxed text-zinc-500" data-testid="join-verification-meaning">
              {meta.verification_meaning}
            </p>
          </aside>
        </div>

        <div className="mt-16 flex flex-wrap gap-4">
          <Link to="/professionals" className="btn-ghost" data-testid="join-to-register">
            See the register and the 19 regulated services
          </Link>
        </div>
      </div>
    </div>
  );
}
